import React from 'react';
import { useProject } from '../projects/queries';
import { useAllProjectTranscripts } from '../transcripts/queries';
import { useDescriptors } from '../descriptors/queries';
import { useCodeSidebarCodes } from '../codes/useCodeSidebarCodes';
import {
  useExportCodesVCodesMutation,
  useExportCodesVTranscriptsMutation,
  useExportCodesVDescriptorMutation
} from './mutations';

// Only prefetch lightweight metadata
export function useExportMetadata(projectId) {
  const projectQuery = useProject(projectId);
  const transcriptsQuery = useAllProjectTranscripts(projectId); // Load transcripts for Code x Transcript matrix
  // Use useCodeSidebarCodes with ignoreCollapseState=true to get all codes in correct order
  const { codes, isLoading: codesLoading, error: codesError } = useCodeSidebarCodes(projectId, true);
  const descriptorsQuery = useDescriptors(projectId); // Need descriptors for descriptor matrix export

  return {
    project: projectQuery.data,
    transcripts: transcriptsQuery.data, // Include transcripts for Code x Transcript export
    codes: codes, // Already in flat array format with position, parent_id, and level
    descriptors: descriptorsQuery.data?.descriptors, // Include descriptor metadata
    isLoading: projectQuery.isLoading || transcriptsQuery.isLoading || codesLoading || descriptorsQuery.isLoading,
    error: projectQuery.error || transcriptsQuery.error || codesError || descriptorsQuery.error
  };
}

// Hook for matrix export actions only
export function useExportMatrixActions(projectId) {
  const { project, transcripts, codes, descriptors, isLoading: metadataLoading, error: metadataError } = useExportMetadata(projectId);

  // Add transcripts to project object for backward compatibility with export mappers
  const projectWithTranscripts = project ? { ...project, transcripts: transcripts || [] } : project;

  const codesVCodesMutation = useExportCodesVCodesMutation(projectWithTranscripts, codes);
  const codesVTranscriptsMutation = useExportCodesVTranscriptsMutation(projectWithTranscripts, codes);
  const codesVDescriptorMutation = useExportCodesVDescriptorMutation(projectWithTranscripts, codes, descriptors);

  return {
    // Metadata
    project: projectWithTranscripts,
    codes,
    descriptors,
    isMetadataLoading: metadataLoading,
    metadataError,

    // Export actions
    exportCodesVCodes: (codeIds) => codesVCodesMutation.mutate(codeIds),
    exportCodesVTranscripts: () => codesVTranscriptsMutation.mutate(),
    exportCodesVDescriptor: () => codesVDescriptorMutation.mutate(),

    // Loading states for UI
    isExportingCodesVCodes: codesVCodesMutation.isPending,
    isExportingCodesVTranscripts: codesVTranscriptsMutation.isPending,
    isExportingCodesVDescriptor: codesVDescriptorMutation.isPending,

    // Error states
    codesVCodesError: codesVCodesMutation.error,
    codesVTranscriptsError: codesVTranscriptsMutation.error,
    codesVDescriptorError: codesVDescriptorMutation.error,

    // Ready state
    isReady: !metadataLoading && !metadataError && !!project && !!codes
  };
}